import { useEffect, useRef } from "react";
import { useEscapeEbene } from "./useEscapeEbene.js";
const FOKUSIERBAR_SELEKTOR = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), ' +
    'select:not([disabled]), [tabindex]:not([tabindex="-1"])';
function fokussierbareElemente(container) {
    return Array.from(container.querySelectorAll(FOKUSIERBAR_SELEKTOR));
}
/**
 * Kleiner, wiederverwendbarer Fokus-Baustein für Overlays (Modals, Popover-Menüs, …) —
 * keine Bibliothek, nur die vier Grundregeln eines zugänglichen Dialogs:
 *
 *  1. Beim Öffnen wandert der Fokus in den Container (auf dessen erstes bedienbares
 *     Element, ersatzweise auf den Container selbst — dafür braucht der Container ein
 *     eigenes `tabIndex={-1}`).
 *  2. Die Tabulatortaste bleibt darin gefangen und läuft am Rand wieder um.
 *  3. Escape löst `onClose` aus — über den gemeinsamen Ebenenstapel (useEscapeEbene) und
 *     nicht über einen eigenen Horcher. Ein Dialog ist die oberste Schicht, solange er
 *     offen ist, und nimmt das Escape damit allem darunter weg; ein Menü oder ein
 *     überlagernder Rand unter ihm bleibt stehen. Bis zum 10.09.2026 stand hier ein
 *     `stopPropagation` am eigenen Rahmen — das wirkte nur, solange der Fokus wirklich im
 *     Dialog stand, und gar nicht gegen Horcher am selben Ziel.
 *  4. Beim Schließen kehrt der Fokus dorthin zurück, wo er vorher stand.
 *
 * Der Aufrufer bringt sein eigenes Markup mit (Rolle, `aria-modal`, `aria-label` — siehe
 * App.tsx) und übergibt nur eine Ref auf den Rahmen, der den Fokus fangen soll.
 */
export function useFocusTrap(aktiv, containerRef, onClose) {
    const vorherigerFokus = useRef(null);
    // Fixrunde 2, Befund 1: Ein Aufrufer übergibt onClose fast immer als Inline-Funktion —
    // bei jedem Neuzeichnen eine neue Identität. Stünde onClose in den Abhängigkeiten des
    // Effekts unten, liefe der bei JEDER Zustandsänderung im Dialoginhalt neu an (nicht nur
    // beim Öffnen/Schließen) und würde den Fokus jedes Mal auf das erste Element
    // zurückwerfen. onCloseRef hält deshalb immer den aktuellsten Rückruf, ohne dass seine
    // Identität den Effekt beeinflusst — die Zuweisung während des Renderns (nicht in einem
    // eigenen Effekt) stellt sicher, dass sie vor jedem möglichen Tastaturereignis aktuell
    // ist. Der Baustein soll bei unachtsamer Benutzung (kein eigenes useCallback an der
    // Aufrufstelle) nicht stillschweigend das Falsche tun.
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    // Die Escape-Schicht dieses Dialogs. Sie hängt an `aktiv` und nicht am Rahmen: Ein
    // Dialog, der noch keinen Rahmen hat, hat auch nichts zu schliessen.
    useEscapeEbene(aktiv, () => onCloseRef.current());
    useEffect(() => {
        if (!aktiv)
            return;
        const container = containerRef.current;
        if (container === null)
            return;
        vorherigerFokus.current = document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
        const erstes = fokussierbareElemente(container)[0] ?? container;
        erstes.focus();
        // Nur die Tabulatorfalle. Escape läuft über den Ebenenstapel (siehe oben).
        function onKeyDown(ereignis) {
            if (ereignis.key !== "Tab" || container === null)
                return;
            const elemente = fokussierbareElemente(container);
            if (elemente.length === 0) {
                ereignis.preventDefault();
                return;
            }
            const erstesElement = elemente[0];
            const letztesElement = elemente[elemente.length - 1];
            if (ereignis.shiftKey && document.activeElement === erstesElement) {
                ereignis.preventDefault();
                letztesElement.focus();
            }
            else if (!ereignis.shiftKey && document.activeElement === letztesElement) {
                ereignis.preventDefault();
                erstesElement.focus();
            }
        }
        container.addEventListener("keydown", onKeyDown);
        return () => {
            container.removeEventListener("keydown", onKeyDown);
            // Fokus zurückgeben — aber nur, wenn das ursprüngliche Element noch im Dokument
            // hängt; ein inzwischen entferntes Element ließe .focus() sonst wirkungslos verpuffen.
            if (vorherigerFokus.current !== null && document.contains(vorherigerFokus.current)) {
                vorherigerFokus.current.focus();
            }
        };
        // onClose bewusst NICHT in dieser Liste — siehe onCloseRef oben. containerRef ist ein
        // von useRef zurückgegebenes, über Neuzeichnungen hinweg stabiles Objekt und löst
        // deshalb ohnehin nie einen erneuten Lauf aus; es steht hier nur der Vollständigkeit
        // halber, nicht weil es das Verhalten ändern würde.
    }, [aktiv, containerRef]);
}
//# sourceMappingURL=useFocusTrap.js.map